"use client";

import posthog from "posthog-js";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

import { usePathname } from "next/navigation";

import { TimezoneScopeContext } from "@/contexts/timezone-scope";

import { parseError } from "@/lib/parse-error";
import {
  isValidTimezone,
  setGlobalTenantDefaultTimezone,
} from "@/lib/timezone";
import { toast } from "@/lib/use-toast";

import { PatientSummary } from "@/types/patient";
import { Tenant } from "@/types/tenant";

import { removeDynamicHeader, setDynamicHeader } from "@/api/api-config";
import { getPatientMe } from "@/api/patient/portal";
import { fetchTenant } from "@/api/tenant";
import { authService } from "@/services/auth-service";

interface PatientPortalPreviewState {
  isPreview: boolean;
  token: string | null;
}

interface PatientPortalContextValue {
  patient: PatientSummary | null;
  tenant: Tenant | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refetchPatient: () => Promise<void>;
  preview: PatientPortalPreviewState;
}

const PatientPortalContext = createContext<PatientPortalContextValue | null>(
  null,
);

const PATIENT_PORTAL_BLACKLIST_DETAIL =
  "This patient cannot access the patient portal.";
const SCHEDULING_BLACKLIST_TOAST = {
  title: "On Scheduling Blacklist",
  description:
    "You are on the scheduling blacklist. Please contact the clinic to book an appointment.",
} as const;
const PREVIEW_HEADER = "X-Patient-Portal-Preview";
const PREVIEW_QUERY_PARAM = "preview";
// Registered as a PostHog super property for the lifetime of a preview
// session, so every event captured while previewing — custom and
// autocapture/pageview alike — carries the flag. The
// `patient_portal_preview` global analytics exclusion
// (decoda/internal/analytics/exclusions/patient_portal_preview.py) uses
// it to keep staff QA traffic out of patient-facing portal metrics.
const PREVIEW_SUPER_PROPERTY = "patient_portal_preview";
const previewStorageKey = (tenantName: string | undefined) =>
  `patient-portal-preview:${tenantName ?? "unknown"}`;

function removePreviewTokenFromUrl() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(PREVIEW_QUERY_PARAM)) return;
  url.searchParams.delete(PREVIEW_QUERY_PARAM);
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}

async function handlePatientPortalAccessError(error: unknown) {
  const description = await parseError(error, "");
  if (
    (error as any)?.response?.status === 403 &&
    description === PATIENT_PORTAL_BLACKLIST_DETAIL
  ) {
    toast({
      ...SCHEDULING_BLACKLIST_TOAST,
      variant: "error",
    });
    try {
      await authService.logout(true);
    } catch {
      // ignore
    }
  }
}

export function PatientPortalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isTenantResolved, setIsTenantResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [locationSearch, setLocationSearch] = useState(() =>
    typeof window === "undefined" ? "" : window.location.search,
  );
  const [isPreviewResolved, setIsPreviewResolved] = useState(false);
  const [preview, setPreview] = useState<PatientPortalPreviewState>({
    isPreview: false,
    token: null,
  });

  useEffect(() => {
    const tenantName = pathname?.split("/")[1];
    if (!tenantName || tenantName === "login") return;
    // A response that lands after the portal unmounted would write the
    // clinic's zone with nobody left to clear it.
    let current = true;
    fetchTenant(tenantName)
      .then((data) => {
        if (!current || !data) return;
        // Before the render it triggers, so nothing in the portal is drawn
        // on the device's clock once the clinic's is known. The store tier
        // is what `useStoreTimezone()` and the date formatters read.
        setGlobalTenantDefaultTimezone(
          isValidTimezone(data.defaultTimezone) ? data.defaultTimezone : null,
        );
        setTenant(data);
      })
      .finally(() => {
        if (current) setIsTenantResolved(true);
      });
    return () => {
      current = false;
    };
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // React calls history.replaceState during its insertion-effect phase, and a
    // setState from inside that phase warns ("useInsertionEffect must not
    // schedule updates"). Deferring a microtask lands the update in a normal
    // render pass instead, without changing observable ordering.
    const updateLocationSearch = () => {
      queueMicrotask(() => setLocationSearch(window.location.search));
    };
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function pushState(...args) {
      originalPushState.apply(this, args);
      updateLocationSearch();
    };
    window.history.replaceState = function replaceState(...args) {
      originalReplaceState.apply(this, args);
      updateLocationSearch();
    };

    updateLocationSearch();
    window.addEventListener("popstate", updateLocationSearch);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", updateLocationSearch);
    };
  }, []);

  const isPublicPath =
    pathname?.includes("/patient-portal/login") ||
    pathname?.includes("/patient-portal/sign-up");
  const isProtectedPortalPath =
    !isPublicPath &&
    (pathname?.includes("/patient-portal/") ||
      pathname?.endsWith("/patient-portal"));

  useEffect(() => {
    const tenantName = pathname?.split("/")[1];
    const key = previewStorageKey(tenantName);
    const tokenFromUrl = new URLSearchParams(locationSearch).get(
      PREVIEW_QUERY_PARAM,
    );
    const tokenFromStorage =
      typeof window !== "undefined" ? sessionStorage.getItem(key) : null;
    const token = tokenFromUrl || tokenFromStorage;

    if (isPublicPath || !token) {
      removeDynamicHeader(PREVIEW_HEADER);
      if (typeof window !== "undefined" && isPublicPath) {
        sessionStorage.removeItem(key);
      }
      posthog.unregister(PREVIEW_SUPER_PROPERTY);
      setPreview({ isPreview: false, token: null });
      setIsPreviewResolved(true);
      return;
    }

    if (typeof window !== "undefined") {
      sessionStorage.setItem(key, token);
      if (tokenFromUrl) {
        removePreviewTokenFromUrl();
      }
    }
    setDynamicHeader(PREVIEW_HEADER, token);
    posthog.register({ [PREVIEW_SUPER_PROPERTY]: true });
    setPreview({ isPreview: true, token });
    setIsPreviewResolved(true);

    return () => {
      removeDynamicHeader(PREVIEW_HEADER);
      posthog.unregister(PREVIEW_SUPER_PROPERTY);
    };
  }, [pathname, isPublicPath, locationSearch]);

  // When navigating to a protected portal page, set loading immediately (before paint) to
  // avoid a brief flash where isLoading is still false, which would cause redirect before fetch.
  useLayoutEffect(() => {
    if (isProtectedPortalPath) {
      setIsLoading(true);
    }
  }, [pathname, isProtectedPortalPath]);

  const fetchPatient = useCallback(async () => {
    try {
      const data = await getPatientMe();
      setPatient(data);
    } catch (error) {
      setPatient(null);
      await handlePatientPortalAccessError(error);
    }
  }, []);

  const refetchPatient = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchPatient();
    } finally {
      setIsLoading(false);
    }
  }, [fetchPatient]);

  useEffect(() => {
    if (isPublicPath) {
      setPatient(null);
      setIsLoading(false);
      return;
    }

    if (!isPreviewResolved) {
      setIsLoading(true);
      return;
    }

    // Protected routes: use getPatientMe as single source of truth.
    // Backend validates Firebase — no client-side auth state stitching.
    let mounted = true;
    setIsLoading(true);

    getPatientMe()
      .then((data) => {
        if (mounted) setPatient(data);
      })
      .catch(async (error) => {
        if (mounted) setPatient(null);
        await handlePatientPortalAccessError(error);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [pathname, isPublicPath, isPreviewResolved, preview.token]);

  useEffect(() => {
    if (isPublicPath) return;
    const unsubscribe = authService.addAuthStateListener((user) => {
      if (!user) setPatient(null);
    });
    return unsubscribe;
  }, [isPublicPath]);

  // The portal mounts no TenantProvider, so it puts the clinic's clock in
  // place itself: the store tier (written when the tenant arrives, above) and
  // the scope that shared components reading `useTimezone()` sit under. The
  // scope is always mounted and only its value changes, so the tenant arriving
  // doesn't remount the portal.
  const tenantTimezone = tenant?.defaultTimezone;
  const clinicTimezone = isValidTimezone(tenantTimezone)
    ? tenantTimezone
    : null;
  useEffect(() => () => setGlobalTenantDefaultTimezone(null), []);

  const isAuthenticated = !!patient;
  // Portal pages wait on `isLoading` before showing records, so holding it
  // until the tenant resolves keeps their dates off the device's clock.
  const isPortalLoading =
    isLoading || (isProtectedPortalPath && !isTenantResolved);

  return (
    <PatientPortalContext.Provider
      value={{
        patient,
        tenant,
        isLoading: isPortalLoading,
        isAuthenticated,
        refetchPatient,
        preview,
      }}
    >
      <TimezoneScopeContext.Provider value={clinicTimezone}>
        {children}
      </TimezoneScopeContext.Provider>
    </PatientPortalContext.Provider>
  );
}

export function usePatientPortal() {
  const context = useContext(PatientPortalContext);
  if (context === undefined || context === null) {
    throw new Error(
      "usePatientPortal must be used within PatientPortalProvider",
    );
  }
  return context;
}
